import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	TextField,
	Typography,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import editIcon from "../../../assets/employees-customData-icon.svg";
import { GlobalContext } from "../../../GlobalContext";
import { Context } from "../../table/employees/Context";
import { updateCustomData } from "../../../utils/Employee";

const CustomData = ({ open, setOpen, employeeName, personNumber, customData }) => {
	const { t } = useTranslation("employees.form.custom_data");

	const { handlerAlert } = useContext(GlobalContext);
	const { reloading } = useContext(Context);

	const [editMode, setEditMode] = useState(false);
	const [loading, setLoading] = useState(false);
	const [formValues, setFormValues] = useState({});

	const blackList = JSON.parse(sessionStorage.getItem("custom_data_black_list")) || [];

	const editableKeys = (JSON.parse(sessionStorage.getItem("privileges"))?.settings?.edit_custom_data || "")
		.split(",")
		.map((key) => key.trim())
		.filter(Boolean);

	const entries = customData
		? Object.entries(customData).filter(([key]) => !blackList.includes(key))
		: [];

	const editableEntries = entries.filter(([key]) => editableKeys.includes(key));

	useEffect(() => {
		if (open) {
			setEditMode(false);
			setFormValues(customData ? { ...customData } : {});
		}
	}, [open, customData]);

	const handleSave = async () => {
		const success = await updateCustomData(setLoading, personNumber, formValues, handlerAlert);

		if (success) {
			reloading();
			setOpen(false);
		}
	};

	return (
		<Dialog fullWidth open={open} onClose={() => setOpen(false)}>
			<DialogTitle>
				<Typography variant="h6">{t("custom_data.title")}</Typography>

				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
						<PersonIcon sx={{ width: "32px", height: "32px", color: "#9D9A95" }} />

						<Typography variant="body3" sx={{fontSize: "16px"}}>
							{employeeName}
						</Typography>
					</Box>

					{editableEntries.length > 0 && (
						<Button
							sx={{
								display: "flex",
								fontSize: "16px",
								fontWeight: "700",
								alignItems: "center",
								borderRadius: "48px",
								backgroundColor: "#F0C400",
								color: "#190007",
								height: "36px",
								"&:hover": { backgroundColor: "#F0C400" },
							}}
							endIcon={<img src={editIcon} alt="edit" />}
							onClick={() => setEditMode(true)}
						>
							{t("custom_data.fields.edit")}
						</Button>
					)}
				</Box>
			</DialogTitle>

			<DialogContent dividers>
				{entries.length === 0 ? (
					<Typography variant="body3">{t("custom_data.fields.no_data")}</Typography>
				) : editMode ? (
					<Grid container spacing={1.5}>
						{editableEntries.map(([key]) => (
							<Grid item xs={12} sm={6} key={key}>
								<TextField
									label={key}
									fullWidth
									value={formValues[key] ?? ""}
									onChange={(event) =>
										setFormValues((previous) => ({ ...previous, [key]: event.target.value }))
									}
								/>
							</Grid>
						))}
					</Grid>
				) : (
					<Grid container spacing={1.5}>
						{entries.map(([key, value], index) => (
							<Grid container item xs={12} spacing={1} key={index}>
								<Grid item xs={4}>
									<Typography variant="body3" color="text.secondary" sx={{ wordBreak: "break-word" }}>
										{key}
									</Typography>
								</Grid>

								<Grid item xs={8}>
									<Typography variant="body3" sx={{ wordBreak: "break-word" }}>
										{value}
									</Typography>
								</Grid>
							</Grid>
						))}
					</Grid>
				)}
			</DialogContent>

			<DialogActions>
				{editMode && (
					<Button onClick={handleSave} disabled={loading}>
						{t("custom_data.buttons.save")}
					</Button>
				)}

				<Button onClick={() => setOpen(false)} disabled={loading}>
					{t("custom_data.buttons.close")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export { CustomData };
